// lib/modelist/projectScene.ts
import { canAccessFater3D } from '#lib/access/fater3dAccess';
import { uploadFileToCloud } from '#lib/azure-client';
import { loadImageDataFromUrl } from '#lib/canvas-utils';
import { firstLayerFrameSize } from '#lib/layer-geometry';
import { selectActiveSubjectFromInventoryState } from '#lib/leasing/session-focus';
import * as fater3d from '#lib/modelist/fater3dState';
import { isStreetCuts, isStreetPanoramaFrame, turnStreetCuts } from '#lib/modelist/streetDevelop';
import { bandHeight, rollBand, STREET_BANDS } from '#lib/modelist/streetview';
import type { KernelHandle, KernelState, Layer, ProjectRuntimeState, Rect, StreetBandMark, StreetBandPicture, StreetBandPixels, StreetCuts } from '#types/types';

/**
 * What a Studio project shows, canvas or scene, for its Canvas | Scene switch, the Portal's left column and Studio's
 * paste handler alike; null when it has no scene for this user. The server serves a scene to the project's owner only.
 */
export const projectSurfaceOf = (state: Pick<KernelState, 'userContext' | 'inventory' | 'entityKernelState'>, projectId: string): 'canvas' | 'scene' | null => {
  const project = state.inventory.Project.registry[projectId];
  if (!project || !canAccessFater3D(state.userContext) || project.userId !== state.userContext.dbUserId
    || (project.projectType !== 'FREEFORM' && project.projectType !== 'STORE_SWAP')) return null;
  const runtime = state.entityKernelState[`Project:${projectId}`]?.state?.runtime as ProjectRuntimeState | undefined;
  return runtime?.projectSurface === 'scene' ? 'scene' : 'canvas';
};

type SceneState = Parameters<typeof projectSurfaceOf>[0] & Pick<KernelState, 'fater3d'>;

/**
 * A project's scene for what hosts it (its page, the Portal's two columns): shown, kept mounted and hidden under the
 * canvas, or absent. Kept only once opened and while Fater 3D's one slice is still this project's: past that the
 * hidden space would draw another site, so it leaves and its next showing starts over (fater3d.openScene).
 */
export const projectSceneOf = (state: SceneState, projectId: string): 'shown' | 'kept' | null => {
  const surface = projectSurfaceOf(state, projectId);
  return surface === 'scene' ? 'shown' : surface === 'canvas' && state.fater3d.sceneOf === projectId ? 'kept' : null;
};

/** Whether the open site's space and chat are on screen: the module's always are, a project's scene's only while it is the surface shown. */
export const spaceShown = (state: SceneState): boolean =>
  state.fater3d.sceneOf === null || projectSurfaceOf(state, state.fater3d.sceneOf) === 'scene';

/**
 * A scene's view on its project's canvas, done in the browser as a dropped image is: one workspace of the picture's
 * ratio, named after the view, holding it as a layer. The picture is the view's capture (a url) or the space's own.
 */
export async function addViewToCanvas(kernel: KernelHandle, projectId: string, name: string, picture: string | Blob): Promise<string> {
  const response = typeof picture === 'string' ? await fetch(picture) : null;
  if (response && !response.ok) throw new Error('The view\'s picture could not be read.');
  const blob = response ? await response.blob() : picture as Blob;
  const bitmap = await createImageBitmap(blob);
  const { width, height } = bitmap;
  // The layer's pixels are read before the workspace exists: a picture that cannot be read leaves no empty workspace
  // of the view's name behind, and a retry no second one. Nothing after this line waits or reads.
  const context = new OffscreenCanvas(width, height).getContext('2d');
  context?.drawImage(bitmap, 0, 0);
  bitmap.close();
  const pixels = context?.getImageData(0, 0, width, height);
  if (!pixels) throw new Error('The view\'s picture could not be read.');
  // Its frame is the one its first layer gives it. The canvas is not on screen while the scene is: nothing to zoom.
  const workspaceId = kernel.handleAddWorkspace(projectId, { frameSize: firstLayerFrameSize(width, height), name, zoomToFit: false, logSource: 'fater3d:view' });
  if (!workspaceId) throw new Error('The workspace could not be created.');
  // As a file's layer (addLayerFromFile), which decodes after the workspace exists.
  kernel.addLayerFromImageData(workspaceId, pixels, { name, source: 'file', isAltered: false, pulseToken: Date.now() });
  kernel.invalidateWorkspaceVersionCache(workspaceId);
  return workspaceId;
}

/** Where a latitude band lies on a 360° workspace's frame of height `h`: latitude 90 at the top, linearly. */
const bandSpan = (h: number, [from, to]: readonly [number, number]) => ({ y: (h * (90 - to)) / 180, h: (h * (to - from)) / 180 });

/**
 * A Street View point on its project's canvas: one workspace framing the whole 2:1 panorama, each latitude band a
 * layer at its own pixels placed on its latitudes, in the order given (the last on top). The pixels are all there
 * before the workspace exists. Resolves once the workspace's row exists on the server, which hides a point whose
 * workspace it cannot find (streetview.server.ts).
 */
export async function addStreetBandsToCanvas(kernel: KernelHandle, projectId: string, name: string, bands: StreetBandPixels[]): Promise<string> {
  const frame = firstLayerFrameSize(2, 1);
  let persisted: Promise<void> | undefined;
  const workspaceId = kernel.handleAddWorkspace(projectId, { frameSize: frame, name, zoomToFit: false, logSource: 'fater3d:street', onPersisted: (written) => { persisted = written; } });
  if (!workspaceId || !persisted) throw new Error('The workspace could not be created.');
  // One layer per band: side-by-side layers showed a seam on the canvas (studio-layer-tiles.ts), a 12K one is drawn whole.
  for (const band of bands) {
    kernel.addLayerFromImageData(workspaceId, band.pixels, { name: `${name} · ${band.name}`, source: 'file', isAltered: false, pulseToken: Date.now(),
      params: { streetBand: { band: band.name, tile: 0, seam: 0 } },
      rect: { x: 0, w: frame.width, ...bandSpan(frame.height, band.lat) }, scale: frame.width / band.pixels.width });
  }
  kernel.invalidateWorkspaceVersionCache(workspaceId);
  await persisted.catch((cause) => { throw new Error('The workspace could not be saved.', { cause }); });
  return workspaceId;
}

// A workspace made before its band layers were marked is read by the names addStreetBandsToCanvas gave them.
const STREET_BAND_NAME = / · (sky|ground|horizon)(?: (\d+))?$/;

/** Which band of a Street View workspace a layer is, null for any other layer. */
export function readStreetBand(layer: Pick<Layer, 'name' | 'params'>): StreetBandMark | null {
  const mark = (layer.params as { streetBand?: Partial<StreetBandMark> } | undefined)?.streetBand;
  if (mark && (mark.band === 'sky' || mark.band === 'ground' || mark.band === 'horizon') && Number.isInteger(mark.tile) && mark.tile! >= 0) {
    return { band: mark.band, tile: mark.tile!, seam: Number.isFinite(mark.seam) ? mark.seam! : 0, ...(isStreetCuts(mark.cuts) ? { cuts: { at: mark.cuts.at, notes: mark.cuts.notes } } : {}) };
  }
  const named = STREET_BAND_NAME.exec(layer.name ?? '');
  if (!named || (named[2] && named[1] !== 'horizon')) return null;
  return { band: named[1] as StreetBandMark['band'], tile: named[2] ? Number(named[2]) - 1 : 0, seam: 0 };
}

/** The Develop 360 cuts a Street View workspace's band layers remember, null when none were validated on it. */
export const rememberedStreetCuts = (layers: Layer[]): StreetCuts | null => layers.map(readStreetBand).find((mark) => mark?.cuts)?.cuts ?? null;

/** Remembers Develop 360's cuts on every band layer of a Street View workspace, saved as any layer change, outside the undo history. */
export function rememberStreetCuts(kernel: KernelHandle, workspaceId: string, cuts: StreetCuts): void {
  const saves: Promise<unknown>[] = [];
  for (const layer of kernel.getLayersInWorkspaceContext(workspaceId)) {
    const mark = readStreetBand(layer);
    if (!mark) continue;
    kernel.setLayer(layer.id, (existing) => ({ ...existing, params: { ...existing.params, streetBand: { ...mark, cuts } } }));
    saves.push(kernel.saveLayer(layer.id));
  }
  void Promise.all(saves).catch(() => kernel.handleError('The 360° cuts could not be saved.'));
}

/**
 * A Street View workspace's band layers, the horizon's one layer or its tiles left to right, or why its seam cannot be set: only while
 * it holds nothing else (owner, 2026-10-14), since any other layer would no longer line up with the turned panorama.
 */
export function streetSeamBands(layers: Layer[], frame: Rect): { sky: Layer; ground: Layer; horizon: Layer[]; seam: number } | string {
  if (!isStreetPanoramaFrame(frame)) return 'This workspace is not a 360° panorama.';
  const marked = layers.map((layer) => ({ layer, mark: readStreetBand(layer) }));
  if (marked.some(({ layer, mark }) => !mark || layer.type === 'video' || layer.type === 'audio' || layer.type === 'svg')) {
    return 'The seam can only be set while the workspace holds nothing but its 360° bands: its other layers would no longer line up.';
  }
  const of = (band: StreetBandMark['band']) => marked.filter(({ mark }) => mark!.band === band).sort((a, b) => a.mark!.tile - b.mark!.tile);
  const [sky, ground, horizon] = [of('sky'), of('ground'), of('horizon')];
  if (sky.length !== 1 || ground.length !== 1 || !horizon.length || horizon.some(({ mark }, i) => mark!.tile !== i)) {
    return 'A band of the 360° is missing from the workspace.';
  }
  return { sky: sky[0]!.layer, ground: ground[0]!.layer, horizon: horizon.map(({ layer }) => layer), seam: sky[0]!.mark!.seam };
}

/**
 * Sets a Street View workspace's 360° seam at `fraction` of its frame's width: every band is turned so that column
 * becomes its left edge, the layers keeping their rects, in one undoable action. The turn is snapped to a column of the
 * narrowest band so that every band turns by whole pixels alike, and added to the seam each band layer keeps, which the
 * Street View viewer turns back (streetWorkspacePicture); the Develop 360 cuts they remember turn with them. False when
 * the click snapped to the left edge: nothing to turn.
 */
export async function setStreetSeam(kernel: KernelHandle, workspaceId: string, fraction: number): Promise<boolean> {
  const read = () => {
    const workspace = kernel.getWorkspace(workspaceId);
    return workspace ? streetSeamBands(kernel.getLayersInWorkspaceContext(workspaceId), workspace.frameRect) : 'The workspace no longer exists.';
  };
  const bands = read();
  if (typeof bands === 'string') throw new Error(bands);
  const groups = [[bands.sky], [bands.ground], bands.horizon];
  const layers = groups.flat();
  const pixels = new Map<string, ImageData>();
  for (const layer of layers) {
    const decoded = layer.imageData ?? (layer.imageSrc ? await loadImageDataFromUrl(layer.imageSrc).catch(() => null) : null);
    if (!decoded) throw new Error('A band of the 360° could not be read.');
    pixels.set(layer.id, decoded);
  }
  const narrowest = Math.min(...groups.map((g) => g.reduce((sum, l) => sum + pixels.get(l.id)!.width, 0)));
  const turn = Math.round(fraction * narrowest) / narrowest;
  if (!(turn > 0 && turn < 1)) return false;
  const rolled = new Map(groups.flatMap((g) => {
    const next = rollBand(g.map((l) => pixels.get(l.id)!), turn);
    return g.map((l, i) => [l.id, next[i]!] as const);
  }));
  // The pixels may take seconds to read: a band edited meanwhile would be overwritten.
  const now = read();
  if (typeof now === 'string' || [now.sky, now.ground, ...now.horizon].some((l, i) => l.id !== layers[i]!.id
    || l.imageSrc !== layers[i]!.imageSrc || l.contentVersion !== layers[i]!.contentVersion)) {
    throw new Error('The workspace changed while its seam was being set.');
  }
  const seam = (bands.seam + turn * 360) % 360;
  const remembered = rememberedStreetCuts(layers);
  const cuts = remembered ? { cuts: turnStreetCuts(remembered, turn) } : {};
  const states = layers.map((layer) => {
    const { band, tile } = readStreetBand(layer)!;
    return {
      id: layer.id,
      undo: { imageData: pixels.get(layer.id)!, imageSrc: layer.imageSrc, isAltered: layer.isAltered, loadError: layer.loadError, contentVersion: layer.contentVersion, params: layer.params },
      redo: { imageData: rolled.get(layer.id)!, imageSrc: undefined, isAltered: true, loadError: false, contentVersion: (layer.contentVersion ?? 0) + 1,
        params: { ...layer.params, streetBand: { band, tile, seam, ...cuts } } },
    };
  });
  // An upload still running when the other side applies would land its URL over the pixels just restored.
  let uploads = new AbortController();
  const apply = (side: 'undo' | 'redo') => {
    uploads.abort();
    uploads = new AbortController();
    const { signal } = uploads;
    for (const state of states) {
      kernel.setLayer(state.id, (existing) => ({ ...existing, ...state[side] }));
      void kernel.saveLayer(state.id, { signal }).catch(() => kernel.handleError('A band of the 360° could not be saved.'));
    }
    kernel.invalidateWorkspaceVersionCache(workspaceId);
  };
  kernel.executeAction({ description: 'Set the 360° seam', redo: () => apply('redo'), undo: () => apply('undo') });
  return true;
}

const STREET_LAYERS_TIMEOUT_MS = 60_000;
// The project's row can be on screen a moment before its load begins (ProjectPage syncs the row first), its
// workspaces not yet known: an absent workspace is waited for this long, since a load that never begins sets no flag.
const STREET_LOAD_START_MS = 10_000;

/**
 * A Street View point's workspace as it is now, for the inside of its sphere: its frame's latitude bands, each at the
 * pixels of its densest layer there (a frame is 1 Mpx of world units whatever its picture), the band's own size at
 * most, so a one-layer 8K workspace shows its horizon at 8K, not stretched to 12K. Nothing is stored.
 * renderWorkspaceArea skips a layer whose pixels are not decoded, a black sphere right after a reload: the project's
 * load and the workspace's layers are awaited first, and a layer that cannot be read fails the picture. A layer just
 * added has its pixels and no source yet (the save uploads it later): it is drawn, and counts for the size.
 */
export async function streetWorkspacePicture(kernel: KernelHandle, projectId: string, workspaceId: string, signal?: AbortSignal): Promise<StreetBandPicture[]> {
  const asked = Date.now();
  const rasters = () => kernel.getLayersInWorkspaceContext(workspaceId).filter((l) => l.visible && !l.skipRender && l.type !== 'video' && l.type !== 'svg');
  // A point may name any workspace id: one of another project open in this browser is no workspace of this scene's.
  const foreign = () => { const owner = kernel.getInventory().StudioWorkspace.registry[workspaceId]?.projectId; return owner != null && owner !== projectId; };
  const waiting = () => {
    if (foreign()) return false;
    const loading = kernel.readProjectRuntimeState(projectId).isProjectLoading;
    if (loading === true) return true;
    if (loading === undefined && !kernel.getWorkspace(workspaceId)) return Date.now() - asked < STREET_LOAD_START_MS;
    return rasters().some((l) => !!l.imageSrc && !l.imageData && !l.loadError);
  };
  while (waiting()) {
    signal?.throwIfAborted();
    if (Date.now() - asked > STREET_LAYERS_TIMEOUT_MS) throw new Error('The workspace took too long to load.');
    kernel.hydrateVisibleLayers(projectId);
    // ponytail: polled because hydrateVisibleLayers returns nothing and decodes the whole project; the upgrade is an
    // awaitable hydration of one workspace's layers in kernel/useLayers.ts, which this loop then becomes one await of.
    await new Promise((resolve) => setTimeout(resolve, 250));
  }
  signal?.throwIfAborted();
  const workspace = kernel.getWorkspace(workspaceId);
  if (!workspace || foreign()) throw new Error('The workspace no longer exists.');
  const layers = rasters();
  if (!layers.length) throw new Error('The workspace holds no picture.');
  if (layers.some((l) => !!l.imageSrc && !l.imageData)) throw new Error('A layer of the workspace could not be loaded.');
  const { frameRect } = workspace;
  // A hidden band keeps the seam as well as a shown one.
  const seam = kernel.getLayersInWorkspaceContext(workspaceId).map(readStreetBand).find((mark) => mark)?.seam ?? 0;
  const pictures: StreetBandPicture[] = [];
  for (const band of STREET_BANDS) {
    if (pictures.length) signal?.throwIfAborted();
    const span = bandSpan(frameRect.h, band.lat);
    // A layer's rect is the frame's own coordinates.
    const there = layers.filter((l) => l.rect.y < span.y + span.h && l.rect.y + l.rect.h > span.y && l.rect.x < frameRect.w && l.rect.x + l.rect.w > 0);
    const density = Math.max(1, ...there.map((l) => (l.imageData && l.rect.w > 0 ? l.imageData.width / l.rect.w : 1)));
    const rect = { x: frameRect.x, y: frameRect.y + span.y, w: frameRect.w, h: span.h };
    const pixels = Math.min(rect.w * rect.h * density * density, band.width * bandHeight(band.width, band.lat));
    const { file } = await kernel.renderWorkspaceArea(workspaceId, undefined, pixels, false, false, 1, rect, 'jpeg')
      .catch((cause) => { throw new Error('The workspace could not be rendered.', { cause }); });
    if (!file) throw new Error('The workspace could not be rendered.');
    pictures.push({ lat: [band.lat[0], band.lat[1]], blob: file, seam });
  }
  return pictures;
}

// A 4K frame: a larger workspace (an 8K upscale) is scaled down to it.
const REBUILD_PICTURE_MAX_PIXELS = 3840 * 2160;

/**
 * The workspace menu's Powers > Rebuild in 3D: renders the workspace's frame at its own size (a view's capture's, when
 * the workspace was made from one), uploads it, and opens the project's scene, whose chat delivers the picture and
 * the start of a request the user finishes; nothing is sent. The id tells the Modelist which view the workspace is: views.json keeps it
 * (site3d/MODELIST.md). Without a pixel budget the render takes the generation area's density, not the frame's.
 * Rejects, for the caller to say so, only while the request is still the store's and cannot be delivered (no picture,
 * or the project no longer on screen); one the store no longer holds (replaced, delivered since by a later request, or
 * dropped as the user went elsewhere) ends silently.
 */
export async function prepareRebuildMessage(kernel: KernelHandle, projectId: string, workspaceId: string): Promise<void> {
  const workspace = kernel.getWorkspace(workspaceId);
  if (!workspace) throw new Error('The workspace could not be rendered.');
  const id = crypto.randomUUID();
  // The menu is the canvas's: the scene is not on screen when the request is made.
  kernel.setFater3d((f) => fater3d.prepare(f, { id, projectId, attachment: null, sceneOpen: false,
    text: `Look at the attached picture: the workspace "${workspace.name}" (id ${workspaceId}) of this project's canvas, as it is now. Rebuild in 3D ` }));
  try {
    const { frameRect } = workspace;
    const { file } = await kernel.renderWorkspaceArea(workspaceId, undefined, Math.min(frameRect.w * frameRect.h, REBUILD_PICTURE_MAX_PIXELS), false, false, 1, frameRect, 'jpeg');
    const url = file ? await uploadFileToCloud(file, `modelist-ref-${id}.jpg`, 'image/jpeg') : null;
    if (!file || !url) throw new Error('The workspace could not be rendered.');
    if (kernel.getFater3d().prepared?.id !== id) return;
    // Rendering and uploading take seconds: the user may have left the project, whose surface must not flip behind them.
    const state = kernel.getState();
    const subject = selectActiveSubjectFromInventoryState(state);
    if (state.portalStandaloneView !== null || subject?.entityType !== 'Project' || subject.entityId !== projectId) throw new Error('The project was left before the picture landed.');
    kernel.setFater3d((f) => fater3d.landPrepared(f, id, { id, preview: URL.createObjectURL(file), url, name: `${workspace.name}.jpg`, bytes: file.size }));
    kernel.patchProjectRuntimeState(projectId, { projectSurface: 'scene' });
  } catch (error) {
    if (kernel.getFater3d().prepared?.id !== id) return;
    kernel.setFater3d((f) => fater3d.withdrawPrepared(f, id));
    throw error;
  }
}
